import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { v4 as uuid } from 'uuid';
import { getBooths } from '../../api/boothApi';
import { createTeleportButton } from '../../api/teleportApi';
import { useToast } from '../../context/ToastContext';
import DropdownTextField from '../form-components/DropdownTextField';
import MapSpotSelector from '../common/MapSpotSelector';
import { selectFieldSx, dropdownMenuProps } from '../../utils/dropdownStyles';
import { hasRequiredName } from '../../utils/validators';
import IconPickerModal from '../booth/IconPickerModal';
import { IconMap } from '../../utils/IconMap';

const MODE_BOOTH = 'booth';
const MODE_NEW   = 'new';

/**
 * Modal to add a teleport button to the table.
 *
 * Path A (mode=booth): picks an existing booth → copies all its localized values under a new key.
 *   Each teleport button gets its own i18n rows so deleting the booth doesn't affect it.
 * Path B (mode=new):   custom name + existing map spot.
 *
 * Props:
 *   open      — boolean
 *   tableId   — teleport table id
 *   usedMapSpotIds          — spots already tied to a teleport button (drives the
 *                              "Link to Booth" list, hiding booths that already have one)
 *   usedMapSpotIdsForNewSpot — spots occupied by ANY booth or teleport button (drives the
 *                              "Create New" spot picker — a spot can't serve both a booth
 *                              and a standalone button)
 *   onClose   — callback
 *   onSuccess — callback after successful creation
 */
const AddTeleportButtonModal = ({
  open,
  tableId,
  usedMapSpotIds = new Set(),
  usedMapSpotIdsForNewSpot = new Set(),
  onClose,
  onSuccess,
}) => {
  const { toast } = useToast();

  const [mode,           setMode]          = useState(MODE_BOOTH);
  const [booths,         setBooths]        = useState([]);
  const [boothsLoading,  setBoothsLoading] = useState(false);
  const [selectedBooth,  setSelectedBooth] = useState(null);
  const [mapSpotId,      setMapSpotId]     = useState('');
  const [localizedPairs, setLocalizedPairs] = useState({ key: uuid(), values: [] });
  const [saving,         setSaving]        = useState(false);
  // Only used in MODE_NEW — MODE_BOOTH always inherits the selected booth's own iconKey.
  const [iconKey,        setIconKey]       = useState('');
  const [pickerOpen,     setPickerOpen]    = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(MODE_BOOTH);
    setSelectedBooth(null);
    setMapSpotId('');
    setLocalizedPairs({ key: uuid(), values: [] });
    setIconKey('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setBoothsLoading(true);
    getBooths()
      .then((data) => setBooths(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setBoothsLoading(false));
  }, [open]);

  const handleBoothSelect = (e) => {
    const id = e.target.value;
    const booth = booths.find((b) => b.id === id);
    setSelectedBooth(booth ?? null);
  };

  const isValid = () => {
    if (mode === MODE_BOOTH) return !!selectedBooth;
    return !!mapSpotId && hasRequiredName(localizedPairs?.values);
  };

  const handleSave = async () => {
    if (!tableId) return;
    setSaving(true);
    try {
      let payload;

      if (mode === MODE_BOOTH) {
        // Path A: copy all booth locale values under a new key so the teleport button
        // has its own independent i18n rows — not linked to the booth's key. The icon
        // DOES carry over explicitly (iconKey), since without it the button falls back
        // to deriving its icon from the name text, which breaks for any icon whose
        // label doesn't exactly match the booth's display name (e.g. QPR-only icons).
        payload = {
          mapSpotId: selectedBooth.mapSpotId,
          isActive:  true,
          iconKey:   selectedBooth.iconKey ?? null,
          localizedPairs: {
            key:    uuid(),
            values: selectedBooth.localizedPairs?.values ?? [],
          },
        };
      } else {
        // Path B: brand-new key + custom i18n values + explicitly picked icon
        payload = {
          mapSpotId,
          isActive: true,
          iconKey:  iconKey || null,
          localizedPairs,
        };
      }

      await createTeleportButton(tableId, payload);
      toast.success('Teleport button added.');
      onSuccess?.();
    } catch (err) {
      const msg = err?.response?.data?.message
        ?? (err?.response?.status === 409
          ? 'That map spot already has a button in this table.'
          : 'Failed to add button. Please try again.');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const availableBooths = booths.filter((b) => b.contentStatus === 'approved' && !usedMapSpotIds.has(b.mapSpotId));

  const boothName = (b) =>
    b?.localizedPairs?.values?.find((v) => v.localeId?.startsWith('en'))?.value
    ?? b?.localizedPairs?.values?.[0]?.value
    ?? `Booth ${b?.id}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', pr: 6, py: 2.5 }}>
        <Typography variant="h6" fontWeight={700} component="span">
          Add Teleport Button
        </Typography>
        <IconButton
          onClick={(e) => { e.currentTarget.blur(); onClose(); }}
          size="small"
          sx={{ position: 'absolute', right: 10, top: 12, color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2.5}>

          {/* Mode selector */}
          <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel value={MODE_BOOTH} control={<Radio />} label="Link to Booth" />
            <FormControlLabel value={MODE_NEW}   control={<Radio />} label="Create New" />
          </RadioGroup>

          {/* ── PATH A: Link to Booth ── */}
          {mode === MODE_BOOTH && (
            <Box display="flex" flexDirection="column" gap={1.5}>
              <FormControl fullWidth>
                <InputLabel>Select Booth <span style={{ color: '#d32f2f' }}>*</span></InputLabel>
                {boothsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Loading booths…</Typography>
                  </Box>
                ) : (
                  <Select
                    label="Select Booth"
                    value={selectedBooth?.id ?? ''}
                    onChange={handleBoothSelect}
                    sx={selectFieldSx}
                    MenuProps={dropdownMenuProps}
                  >
                    {availableBooths.length === 0 ? (
                      <MenuItem disabled value="">All booths already have a teleport button</MenuItem>
                    ) : (
                      availableBooths.map((b) => (
                        <MenuItem key={b.id} value={b.id}>{boothName(b)}</MenuItem>
                      ))
                    )}
                  </Select>
                )}
              </FormControl>

              {selectedBooth && (
                <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Auto-filled from booth:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Map Spot:</strong> #{selectedBooth.mapSpotId} ({selectedBooth.mapSpot?.x}, {selectedBooth.mapSpot?.y}, {selectedBooth.mapSpot?.z})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Names (all locales):
                  </Typography>
                  {selectedBooth.localizedPairs?.values?.length > 0 ? (
                    selectedBooth.localizedPairs.values.map((v) => (
                      <Typography key={v.localeId} variant="body2">
                        <strong>{v.localeId}:</strong> {v.value}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">No localized names found.</Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* ── PATH B: Create New ── */}
          {mode === MODE_NEW && (
            <Box display="flex" flexDirection="column" gap={2}>
              <MapSpotSelector
                value={mapSpotId}
                onChange={(id) => setMapSpotId(id)}
                required
                disabledIds={usedMapSpotIdsForNewSpot}
              />
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', mb: -1 }}>
                Button Name <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>
              </Typography>
              <DropdownTextField
                label="Button Name"
                values={localizedPairs?.values}
                onChange={(newValues) => setLocalizedPairs((prev) => ({ ...prev, values: newValues }))}
                attribute="value"
                required
              />
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', mb: -1 }}>
                Icon
              </Typography>
              <Box
                onClick={() => setPickerOpen(true)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
                  border: '1px solid', borderColor: 'grey.300', borderRadius: 2, p: 1.25,
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(10,50,109,0.03)' },
                }}
              >
                <Box sx={{
                  bgcolor: '#0a326d', borderRadius: '10px', width: 40, height: 40,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {iconKey && (
                    <Box component="img" src={IconMap[`btn_tt1_${iconKey}`]} alt="" sx={{ width: 24, height: 24 }} />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: iconKey ? 'text.primary' : 'text.secondary' }}>
                  {iconKey ? 'Change icon' : 'Choose an icon (optional)'}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <IconPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={iconKey}
        onSelect={setIconKey}
        prefix="btn_tt1"
      />

      <DialogActions sx={{ justifyContent: 'center', p: 3, bgcolor: 'grey.50' }}>
        <Button onClick={onClose} sx={{ mr: 2 }}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !isValid()}
          size="large"
          sx={{ px: 6, py: 1.5, fontWeight: 700, borderRadius: 2 }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Add Button'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTeleportButtonModal;
